/* Alyus sections use the original Impulse AOS scroll engine and rise-up/fade-in
   keyframes. Keep entrances independent of video, tabs, cart and header code. */
(() => {
  'use strict';
  const media = window.matchMedia('(prefers-reduced-motion: reduce)');
  const pendingImages = new Map();
  const groups = '.aa-hero-copy, .aa-heading, .aa-about > div:last-child, .aa-readable, .aa-career-row > div:last-child, .aa-banner-copy, .aa-faq > div, .aa-proof-grid, .aa-offers, .aa-video-grid, .aa-reviews, .aa-lessons, .section-header, .aa-format-info, .rte';

  function imageReady(el) {
    if (el.classList.contains('aa-motion-ready') || pendingImages.has(el)) return;
    const stage = el.closest('.aa-hero, .aa-banner');
    const imageContainer = stage || (['background', 'fade'].includes(el.dataset.aaMotion) ? el : null);
    const img = el.matches('img') ? el : imageContainer?.querySelector('img');
    function stop() {
      img?.removeEventListener('load', ready);
      img?.removeEventListener('error', ready);
      pendingImages.delete(el);
    }
    function ready() {
      stop();
      el.classList.add('aa-motion-ready');
      window.AOS?.refresh();
    }
    if (!img || img.complete) ready();
    else {
      pendingImages.set(el, stop);
      img.addEventListener('load', ready, {once: true});
      img.addEventListener('error', ready, {once: true});
    }
  }

  function init(root = document) {
    // Content remains readable if the original vendor bundle cannot load.
    if (!window.AOS?.refreshHard) return;
    const targets = new Map();
    const add = (selector, motion) => root.querySelectorAll(selector).forEach(el => {
      if (!el.closest('.shopify-app-block, .collapsible-content, [data-modal="true"]')) targets.set(el, motion);
    });
    root.querySelectorAll('.aa-heading, .aa-about > div:last-child, .aa-story .aa-readable, .aa-career-row > div:last-child, .aa-faq > div, .aa-banner-copy').forEach(el => el.removeAttribute('data-aa-reveal'));
    add('[data-aa-reveal]', 'rise');
    add('.aa-hero-copy > *, .aa-heading p, .aa-heading h2, .aa-about > div:last-child > .aa-eyebrow, .aa-about > div:last-child > h2, .aa-about > div:last-child > .aa-button, .aa-about-copy > *, .aa-story-copy > *, .aa-career-row > div:last-child > .aa-eyebrow, .aa-career-row h3, .aa-career-org, .aa-career-description, .aa-career-row .aa-text-link, .aa-banner-copy > *, .aa-faq > div > .aa-eyebrow, .aa-faq > div > h2, .aa-faq > div > p, .aa-faq details, .aa-intro, .aa-wholesome, .aa-other, .aa-readable > h2, .aa-readable > h3, .aa-readable > p, .aa-readable > ul, .aa-readable > ol, .aa-readable > blockquote', 'rise');
    add('.product-section[data-modal="false"] .product-single__title, .product-section[data-modal="false"] .product-block > .rte > h2, .product-section[data-modal="false"] .product-block > .rte > h3, .product-section[data-modal="false"] .product-block > .rte > p, .product-section[data-modal="false"] .product-block > .rte > ul, .product-section[data-modal="false"] .aa-format-info > *', 'rise');
    add('.aa-portrait, .aa-career-image, .aa-readable > figure, .aa-readable > img, .aa-readable > picture', 'fade');
    root.querySelectorAll('.aa-readable > p').forEach(el => {if (el.querySelector('img')) targets.set(el, 'fade');});
    root.querySelectorAll('.section-header > *, form.contact-form').forEach(el => {
      if (el.closest('#MainContent') && !el.closest('.shopify-app-block')) targets.set(el, el.matches('form') ? 'fade' : 'rise');
    });
    add('.aa-hero-image, .aa-offers-background, .aa-videos-background, .aa-banner picture', 'background');
    add('.aa-hero-copy > h1, .aa-banner-copy > h2', 'title');
    add('.aa-hero-copy > .aa-eyebrow, .aa-hero-copy > .aa-lead, .aa-banner-copy > .aa-eyebrow', 'subtitle');
    add('.aa-hero-copy > .aa-actions, .aa-banner-copy > .aa-button', 'action');

    const positions = new Map();
    targets.forEach((motion, el) => {
      const group = el.closest(groups) || el.parentElement;
      const index = positions.get(group) || 0;
      positions.set(group, index + 1);
      if (el.dataset.aos === 'aa-motion') {imageReady(el);return;}
      // Never take ownership of a legacy animation or animate its child again.
      if (el.hasAttribute('data-aos') || el.parentElement?.closest('[data-aos], [data-aa-motion]')) return;
      el.dataset.aaMotion = motion;
      el.dataset.aos = 'aa-motion';
      el.dataset.aosOnce = 'true';
      el.dataset.aosOffset = '60';
      const delay = {background: 0, fade: 0, title: 300, subtitle: 700, action: 1300}[motion] ?? Math.min(index * 60, 300);
      el.style.setProperty('--aa-delay', `${delay}ms`);
      if (motion === 'title' || motion === 'subtitle') {
        // Same cropper/contents structure as the original slideshow, with
        // scoped classes so legacy layout styles cannot resize the new hero.
        const mask = document.createElement('span'), content = document.createElement('span');
        mask.className = 'aa-motion-mask';content.className = 'aa-motion-content';
        while (el.firstChild) content.append(el.firstChild);
        mask.append(content);el.append(mask);
      }
      if (media.matches) el.classList.add('aa-motion-static');
      imageReady(el);
    });
    document.documentElement.classList.add('aa-native-motion');
    window.AOS.refreshHard();
  }

  function showForAccess(target) {
    let el = target.closest('[data-aos="aa-motion"]');
    while (el) {
      el.classList.add('aa-motion-static', 'aa-motion-ready', 'aos-animate');
      pendingImages.get(el)?.();
      el = el.parentElement?.closest('[data-aos="aa-motion"]');
    }
  }
  document.addEventListener('focusin', event => showForAccess(event.target));
  document.addEventListener('shopify:block:select', event => {
    showForAccess(event.target);
    event.target.querySelectorAll('[data-aos="aa-motion"]').forEach(showForAccess);
  });
  // Remove the mask after the text lands so the existing pink glow stays intact.
  document.addEventListener('animationend', event => {
    if (event.animationName === 'rise-up' && event.target.classList.contains('aa-motion-content')) event.target.parentElement.classList.add('aa-motion-unmasked');
  });
  const reduce = () => {
    if (media.matches) document.querySelectorAll('[data-aos="aa-motion"]').forEach(showForAccess);
  };
  if (media.addEventListener) media.addEventListener('change', reduce);
  else media.addListener(reduce);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('page:loaded', () => init());
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:reorder', () => window.AOS?.refreshHard());
  document.addEventListener('shopify:section:unload', event => {
    pendingImages.forEach((stop, el) => {if (event.target.contains(el)) stop();});
  });
})();
